import {
  useMemo,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from "react";

import { AppStrings } from "../../../../utils/constants/AppStrings";

import {
  buildGamePassColorRows,
  resolveGamePassPalette,
  seedColors,
  stopId,
  type GamePassColorRow,
} from "./gamePassColors";
import { isValidHex } from "./colorPickerPalette";
import ColorPickerPopover from "./ColorPickerPopover";

import styles from "./ColorSection.module.css";

const PlusIcon: FC = () => (
  <svg
    className={styles.plusIcon}
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="M12 6V18M6 12H18"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

export type ColorSectionProps = {
  /**
   * Colour rows to render. Defaults to the live brand palette resolved via
   * {@link resolveGamePassPalette} (falling back to the trivelta defaults); the
   * page passes the same rows it derives once so the left-hand editor and the
   * right-hand preview stay in lock-step.
   */
  rows?: GamePassColorRow[];
  /**
   * Controlled colours map (keyed by {@link stopId}). When omitted the section
   * owns its own state (standalone use / tests); when the page lifts the state
   * up it passes the value + setter so edits drive the live preview.
   */
  colors?: Record<string, string>;
  onColorsChange?: Dispatch<SetStateAction<Record<string, string>>>;
  /** Controlled "Match Trivelta Defaults" toggle (uncontrolled when omitted). */
  matchDefaults?: boolean;
  onMatchDefaultsChange?: (next: boolean) => void;
  /**
   * Locks the whole section (read-only fields, no picker, frozen toggle) while
   * an external write is in flight — the page sets this during a theme save so
   * in-progress edits can't race the merged response that re-seeds the editor.
   */
  disabled?: boolean;
};

/**
 * Game Pass → Style → Color.
 *
 * One row per palette token that trivelta-gamepass-frontend consumes
 * (see {@link buildGamePassColorRows}); gradient tokens expand to one
 * swatch + hex pair per stop. Swatches are seeded from the trivelta default
 * palette (the colours-API response) on first render. Clicking a swatch opens
 * the {@link ColorPickerPopover}; picking a colour — or typing a hex into the
 * field — updates it. Tokens missing from the palette render an empty "+"
 * affordance until a colour is chosen.
 *
 * Turning on "Match Trivelta Defaults" resets every swatch back to the
 * colours-API defaults and locks editing (no picker, read-only fields);
 * turning it off unlocks editing again.
 */
const ColorSection: FC<ColorSectionProps> = ({
  rows: rowsProp,
  colors: colorsProp,
  onColorsChange,
  matchDefaults: matchDefaultsProp,
  onMatchDefaultsChange,
  disabled = false,
}) => {
  const internalRows = useMemo(
    () => buildGamePassColorRows(resolveGamePassPalette()),
    [],
  );
  const rows = rowsProp ?? internalRows;

  const [internalColors, setInternalColors] = useState<Record<string, string>>(
    () => seedColors(rows),
  );
  const colors = colorsProp ?? internalColors;
  const setColors = onColorsChange ?? setInternalColors;

  const [internalMatchDefaults, setInternalMatchDefaults] = useState(false);
  const matchDefaults = matchDefaultsProp ?? internalMatchDefaults;
  const setMatchDefaults = onMatchDefaultsChange ?? setInternalMatchDefaults;

  const [openStopId, setOpenStopId] = useState<string | null>(null);

  // A save in flight (`disabled`) locks editing the same way "Match Trivelta
  // Defaults" does, so no swatch can change under a PATCH whose response will
  // re-seed the editor.
  const locked = matchDefaults || disabled;

  const setStopColor = (id: string, hex: string) =>
    setColors((prev) => ({ ...prev, [id]: hex }));

  const toggleStop = (id: string) => {
    if (locked) return;
    setOpenStopId((prev) => (prev === id ? null : id));
  };

  const selectStopColor = (id: string, hex: string) => {
    setStopColor(id, hex);
    setOpenStopId(null);
  };

  const closePicker = () => setOpenStopId(null);

  const toggleMatchDefaults = () => {
    if (disabled) return;
    const next = !matchDefaults;
    setMatchDefaults(next);
    if (next) {
      setColors(seedColors(rows));
      setOpenStopId(null);
    }
  };

  return (
    <section className={styles.card} data-testid="game-pass-style-color">
      <header className={styles.header}>
        <h2 className={styles.title}>
          {AppStrings.GAME_PASS_STYLE_COLOR_TITLE}
        </h2>

        <div className={styles.matchDefaults}>
          <span className={styles.matchDefaultsLabel}>
            {AppStrings.GAME_PASS_STYLE_COLOR_MATCH_DEFAULTS}
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={matchDefaults}
            aria-label={AppStrings.GAME_PASS_STYLE_COLOR_MATCH_DEFAULTS}
            className={`${styles.switch} ${matchDefaults ? styles.switchOn : ""}`}
            onClick={toggleMatchDefaults}
            disabled={disabled}
          >
            <span className={styles.switchKnob} />
          </button>
        </div>
      </header>

      <div className={styles.rows}>
        {rows.map((row) => {
          const stops = row.stops.length > 0 ? row.stops : [""];

          return (
            <div
              key={row.key}
              className={styles.row}
              data-testid={`game-pass-color-row-${row.key}`}
            >
              <span className={styles.rowLabel}>{row.label}</span>

              <div className={styles.controls}>
                {stops.map((_stop, index) => {
                  const id = stopId(row.key, index);
                  const value = colors[id] ?? "";
                  const filled = isValidHex(value);

                  return (
                    <div key={id} className={styles.controlPair}>
                      <button
                        type="button"
                        className={styles.colorDrop}
                        style={filled ? { backgroundColor: value } : undefined}
                        aria-label={`${row.label} ${AppStrings.GAME_PASS_STYLE_COLOR_PICKER_ARIA}`}
                        onClick={() => toggleStop(id)}
                        disabled={locked}
                      >
                        {!filled && <PlusIcon />}
                      </button>

                      <div className={styles.field}>
                        <input
                          type="text"
                          className={styles.hexInput}
                          value={value}
                          placeholder={
                            AppStrings.GAME_PASS_STYLE_COLOR_HEX_PLACEHOLDER
                          }
                          aria-label={`${row.label} ${AppStrings.GAME_PASS_STYLE_COLOR_HEX_PLACEHOLDER}`}
                          readOnly={locked}
                          onChange={(event) =>
                            setStopColor(id, event.target.value)
                          }
                        />
                      </div>

                      {!locked && openStopId === id && (
                        <ColorPickerPopover
                          value={value}
                          onSelect={(hex) => selectStopColor(id, hex)}
                          onClose={closePicker}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ColorSection;
